import React from "react";

export default function IntroOverlay() {

  // The raw HTML content - structure preserved, circular clipping removed
  const rawHtml = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>NeuralWard Logo Animation</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;700&display=swap');

    :root {
      --secondaryColor: #2d2d37;
      --strokeColor: #ffffff;
      --logoFillColor: #ee2a7b; 
      --textColor: #ffffff;
      --logo-size: min(400px, 35vw);
      --logo-small-size: calc(var(--logo-size) * 0.5);
      --font-size: clamp(2.5rem, 6vw, 6rem);
      --gap: clamp(15px, 3vw, 30px);
      --text-reveal-width: clamp(220px, 35vw, 550px);
    }

    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    html, body {
      height: 100%;
      width: 100%;
      overflow: hidden;
      margin: 0;
      padding: 0;
    }

    body {
      display: flex;
      align-items: center;
      justify-content: center;
      background: var(--secondaryColor);
      font-family: 'Inter', Helvetica, Arial, sans-serif;
      width: 100%;
      height: 100%;
    }

    .main-container {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      position: relative;
      overflow: hidden;
      width: min-content;
      height: 100%;
    }

    .content-wrapper {
      display: inline-flex;
      flex-direction: row;
      align-items: center;
      gap: var(--gap);
      position: relative;
      /* Final layout from the start; the reveal is done with transform + clip-path
         (compositor-only) instead of width/padding animation, so it produces no
         layout shifts (CLS). Same timing/easing = pixel-identical motion. */
      padding-right: var(--logo-small-size);
      transform: translateX(calc((var(--text-reveal-width) + var(--logo-small-size)) / 2));
      clip-path: inset(0 calc(var(--text-reveal-width) + var(--logo-small-size)) 0 0);
      animation: wrapContent 0.8s ease-out 1.4s forwards;
    }

    .logo-container {
      height: var(--logo-size);
      width: var(--logo-size);
      position: relative;
      /* REMOVED: border-radius and overflow:hidden to stop circular clipping */
      background: transparent;
      flex-shrink: 0;
      margin-left: auto;
      transform: translateX(0) scale(1);
      transform-origin: right center;
      animation: resizeOnly 0.8s ease-out 1.4s forwards;
    }

    .text-container {
      width: var(--text-reveal-width);
      font-size: var(--font-size);
      font-weight: 700;
      color: var(--textColor);
      white-space: nowrap;
      flex-shrink: 0;
      opacity: 0;
      animation: expandText 0.8s ease-out 1.4s forwards;
    }

    svg {
      width: 100%;
      height: 100%;
    }

    .stroke-path {
      fill: none;
      stroke: var(--logoFillColor);
      stroke-width: 1.5;
      stroke-linecap: round;
      stroke-linejoin: round;
      stroke-dasharray: 1500;
      stroke-dashoffset: 1500;
      animation: draw 1.8s ease-in-out forwards;
    }

    .fill-path {
      fill: var(--logoFillColor);
      stroke: none;
      opacity: 0;
      animation: fadeIn 0.4s ease-out 1.3s forwards;
    }

    @keyframes draw {
      to { stroke-dashoffset: 0; }
    }

    @keyframes fadeIn {
      to { opacity: 1; }
    }

    @keyframes resizeOnly {
      0% { transform: scale(1); }
      100% { transform: scale(0.5); }
    }

    @keyframes expandText {
      0% { opacity: 0; }
      30% { opacity: 0.5; }
      100% { opacity: 1; }
    }

    @keyframes wrapContent {
      to { transform: translateX(0); clip-path: inset(0 0 0 0); }
    }

    @media (max-width: 768px) {
      :root {
        --logo-size: min(280px, 60vw);
      }

      .content-wrapper {
        flex-direction: column;
        gap: calc(var(--gap) / 4);
        /* reset the desktop transform/clip reveal — mobile keeps its own animation */
        padding-right: 0;
        transform: none;
        clip-path: none;
        animation: wrapContentMobile 0.8s ease-out 1.4s forwards;
      }

      .logo-container {
        margin: 0 auto;
        transform-origin: center;
        animation: resizeOnlyMobile 0.8s ease-out 1.4s forwards;
      }

      .text-container {
        text-align: center;
        width: 100%;
        height: 0;
        overflow: hidden;
        animation: slideTextUpMobile 0.8s ease-out 1.4s forwards;
      }

      @keyframes resizeOnlyMobile {
        0% { transform: scale(1); }
        100% { transform: scale(0.65); }
      }

      @keyframes wrapContentMobile {
        0% { padding-bottom: 0; }
        100% { padding-bottom: calc(var(--logo-size) * 0.2); }
      }

      @keyframes slideTextUpMobile {
        0% { height: 0; opacity: 0; transform: translateY(20px); }
        100% { height: auto; opacity: 1; transform: translateY(0); }
      }
    }
  </style>
</head>
<body>
  <div class="main-container">
    <div class="content-wrapper">
      <div class="logo-container">
        <svg viewBox="0 0 156.2 186.8" xmlns="http://www.w3.org/2000/svg">
          <path class="stroke-path" d="M105,34.34L87.17.26l39.56,11.54c10.12,3.58,19.86,7.37,29.47,12.05l-.07,72.15c0,7.12-1.51,13.6-3.75,20.17-5.67,16.62-17.74,31.38-31.08,42.16-.51-.17-.77-.54-1.12-1.15l-18.15-31.56-37.79-66.8-8.88-15.51-5.13-8.97s-12.82,4.04-22.23,7l.23,55.41c.03,6.6,2.9,12.91,6.04,18.45,5.16,9.09,12.6,15.57,21.02,21.58l.06-46.34,13.61,23.31,33.98,58.04-24.48,14.99-17.65-10.69-7.52-4.77c-19.14-12.38-39.71-30.35-47.84-52.29C2.46,110.97.21,102.87.19,94.12l-.19-69.9,10.1-4.62c10.35-4.73,20.98-7.92,31.88-11.27L69.12,0l5.79,10.61,15.61,28.1,1.23,2.2,29.75,53.76,7.12,13.29.05-66.22Z" />
          <path class="fill-path" d="M105,34.34L87.17.26l39.56,11.54c10.12,3.58,19.86,7.37,29.47,12.05l-.07,72.15c0,7.12-1.51,13.6-3.75,20.17-5.67,16.62-17.74,31.38-31.08,42.16-.51-.17-.77-.54-1.12-1.15l-18.15-31.56-37.79-66.8-8.88-15.51-5.13-8.97s-12.82,4.04-22.23,7l.23,55.41c.03,6.6,2.9,12.91,6.04,18.45,5.16,9.09,12.6,15.57,21.02,21.58l.06-46.34,13.61,23.31,33.98,58.04-24.48,14.99-17.65-10.69-7.52-4.77c-19.14-12.38-39.71-30.35-47.84-52.29C2.46,110.97.21,102.87.19,94.12l-.19-69.9,10.1-4.62c10.35-4.73,20.98-7.92,31.88-11.27L69.12,0l5.79,10.61,15.61,28.1,1.23,2.2,29.75,53.76,7.12,13.29.05-66.22Z" />
        </svg>
      </div>
      <div class="text-container">neuralward</div>
    </div>
  </div>
</body>
</html>
  `;

  return (
    <div className="w-full h-full" style={{ background: "#2d2d37" }}>
      <iframe
        srcDoc={rawHtml}
        style={{
          width: "100%",
          height: "100%",
          border: "none",
          overflow: "hidden",
        }}
        title="Neuralward Intro Animation"
      />
    </div>
  );
}
